/**
 * Presence Admin Dashboard Component
 *
 * Admin-only dashboard showing presence statistics and admin controls
 * Displays total, online, away, busy, and offline user counts
 * Requires Admin role in JWT token
 */

import { forwardRef } from 'react';
import { Stack, Paper, Text, Group, Button, Badge } from '@/components/ui';
import { Grid } from '@mantine/core';
import { usePresenceManager } from '@/hooks/usePresenceManager';
import { toast } from '@/components/ui';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

export interface PresenceAdminDashboardProps {
  /**
   * Dashboard title
   */
  title?: string;
  /**
   * Show paper border
   */
  withBorder?: boolean;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Hide admin actions
   */
  hideActions?: boolean;
}

/**
 * Admin dashboard showing presence statistics and controls
 * Only visible to users with Admin role
 *
 * @example
 * ```tsx
 * <PresenceAdminDashboard />
 * <PresenceAdminDashboard hideActions />
 * ```
 */
export const PresenceAdminDashboard = forwardRef<HTMLDivElement, PresenceAdminDashboardProps>(
  (props, ref) => {
    const {
      title = 'Presence Admin Dashboard',
      withBorder = true,
      className,
      hideActions = false,
    } = props;

    const { isAdmin, presenceStats, refreshStats, manager } = usePresenceManager();

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    // Don't render if not admin or no stats
    if (!isAdmin || !presenceStats) {
      return null;
    }

    const handleSetAllOffline = async () => {
      if (!manager) return;

      if (confirm('Are you sure you want to set all users offline?')) {
        try {
          await manager.setAllUsersOffline();
          await refreshStats();
          toast({
            title: 'Success',
            message: 'All users have been set offline',
            color: 'green',
          });
        } catch (error) {
          toast({
            title: 'Error',
            message: 'Failed to set all users offline',
            color: 'red',
          });
        }
      }
    };

    return (
      <Paper p="md" withBorder={withBorder} ref={ref} className={className}>
        <Stack gap="md">
          <Group justify="space-between">
            <Text size="lg" fw={700} style={accessibilityStyles}>
              {title}
            </Text>
            <Badge color="red" variant="filled">
              ADMIN
            </Badge>
          </Group>

          {/* Stats Grid */}
          <Grid>
            <Grid.Col span={{ base: 12, xs: 6, sm: 4, md: 2.4 }}>
              <Paper p="sm" withBorder>
                <Text size="xs" c="dimmed" style={accessibilityStyles}>
                  Total Users
                </Text>
                <Text size="xl" fw={700} style={accessibilityStyles}>
                  {presenceStats.totalUsers}
                </Text>
              </Paper>
            </Grid.Col>

            <Grid.Col span={{ base: 12, xs: 6, sm: 4, md: 2.4 }}>
              <Paper p="sm" withBorder>
                <Text size="xs" c="dimmed" style={accessibilityStyles}>
                  Online
                </Text>
                <Text size="xl" fw={700} c="green" style={accessibilityStyles}>
                  {presenceStats.onlineUsers}
                </Text>
              </Paper>
            </Grid.Col>

            <Grid.Col span={{ base: 12, xs: 6, sm: 4, md: 2.4 }}>
              <Paper p="sm" withBorder>
                <Text size="xs" c="dimmed" style={accessibilityStyles}>
                  Away
                </Text>
                <Text size="xl" fw={700} c="orange" style={accessibilityStyles}>
                  {presenceStats.awayUsers}
                </Text>
              </Paper>
            </Grid.Col>

            <Grid.Col span={{ base: 12, xs: 6, sm: 4, md: 2.4 }}>
              <Paper p="sm" withBorder>
                <Text size="xs" c="dimmed" style={accessibilityStyles}>
                  Busy
                </Text>
                <Text size="xl" fw={700} c="red" style={accessibilityStyles}>
                  {presenceStats.busyUsers}
                </Text>
              </Paper>
            </Grid.Col>

            <Grid.Col span={{ base: 12, xs: 6, sm: 4, md: 2.4 }}>
              <Paper p="sm" withBorder>
                <Text size="xs" c="dimmed" style={accessibilityStyles}>
                  Offline
                </Text>
                <Text size="xl" fw={700} c="gray" style={accessibilityStyles}>
                  {presenceStats.offlineUsers}
                </Text>
              </Paper>
            </Grid.Col>
          </Grid>

          {/* Admin Actions */}
          {!hideActions && (
            <Group>
              <Button onClick={refreshStats} variant="light">
                Refresh Stats
              </Button>
              <Button onClick={handleSetAllOffline} color="red" variant="light">
                Set All Offline
              </Button>
            </Group>
          )}

          <Text size="xs" c="dimmed" style={accessibilityStyles}>
            Last updated: {new Date(presenceStats.timestamp).toLocaleString()}
          </Text>
        </Stack>
      </Paper>
    );
  }
);

PresenceAdminDashboard.displayName = 'PresenceAdminDashboard';

export default PresenceAdminDashboard;
